import { useState } from 'react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { authApi } from '../../api/auth.api';

const ROLE_OPTIONS = [
  {
    role: 'learner',
    icon: '🎓',
    title: 'I want to learn',
    desc: 'Talk to Lincoln, take quizzes, earn certificates.',
    iconBg: 'rgba(240,184,12,0.18)',
    hoverBorder: 'var(--yellow)',
  },
  {
    role: 'provider',
    icon: '',
    title: 'I want to teach',
    desc: 'Bring your course; Lincoln becomes the tutor.',
    iconBg: 'rgba(34,181,114,0.18)',
    hoverBorder: 'var(--green)',
  },
];

/**
 * AuthModal — sign-in / sign-up flow inside the shared Modal frame.
 *
 * Sign-up is two steps: first the user picks a role via two cards
 * ("I want to learn" / "I want to teach"), then they fill in name + email
 * + password and POST to authApi.register with the chosen role. Sign-in
 * is a single step. The footer link toggles modes. API errors surface
 * inline; on success the modal closes.
 */
export default function AuthModal({ isOpen, onClose, initialMode = 'signin', initialRole = null }) {
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSignup = mode === 'signup';
  const showRolePicker = isSignup && !role;

  let title, subtitle;
  if (showRolePicker) {
    title = 'Join Lincoln School';
    subtitle = 'Tell us what brings you here.';
  } else if (isSignup) {
    title = role === 'learner' ? 'Start learning' : 'Become a provider';
    subtitle = role === 'learner'
      ? 'Free to start. No credit card.'
      : 'Bring your course to a global audience.';
  } else {
    title = 'Welcome back';
    subtitle = 'Sign in to continue your learning journey.';
  }

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const switchMode = (next) => {
    setMode(next);
    setRole(null);
    setError('');
  };

  const close = () => {
    onClose?.();
    // Reset *after* the modal unmounts so users don't see fields wipe.
    setTimeout(() => {
      setForm({ name: '', email: '', password: '' });
      setRole(initialRole);
      setError('');
      setLoading(false);
      setMode(initialMode);
    }, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isSignup) {
        await authApi.register({ ...form, role });
      } else {
        await authApi.login({ email: form.email, password: form.password });
      }
      close();
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.message
        || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={title} size="md">
      {isSignup && role && (
        <button
          type="button"
          onClick={() => setRole(null)}
          className="inline-flex items-center gap-1.5 font-semibold"
          style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
      )}

      <p style={{ marginTop: -4, marginBottom: 20, fontSize: 14, color: 'var(--muted)' }}>
        {subtitle}
      </p>

      {showRolePicker ? (
        <div className="flex flex-col gap-3">
          {ROLE_OPTIONS.map((opt) => (
            <RoleCard key={opt.role} {...opt} onClick={() => setRole(opt.role)} />
          ))}
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3.5">
          {isSignup && (
            <span
              className="inline-flex self-start items-center gap-1.5 rounded-full font-semibold tracking-wide"
              style={{
                padding: '4px 10px',
                fontSize: 11,
                background: role === 'learner' ? 'rgba(240,184,12,0.18)' : 'rgba(34,181,114,0.18)',
                color: role === 'learner' ? '#8a6b00' : 'var(--green-2)',
                marginBottom: 4,
              }}
            >
              {role === 'learner' ? 'Learner' : 'Provider'}
            </span>
          )}

          {isSignup && (
            <Input
              label="Full name"
              name="name"
              value={form.name}
              onChange={update('name')}
              placeholder="Your full name"
              autoComplete="name"
              required
            />
          )}
          <Input
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={update('email')}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
          <Input
            label="Password"
            name="password"
            type="password"
            value={form.password}
            onChange={update('password')}
            placeholder={isSignup ? 'At least 8 characters' : 'Your password'}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            minLength={isSignup ? 8 : undefined}
            required
          />

          {!isSignup && (
            <div className="flex justify-end">
              <a href="#" style={{ fontSize: 12, color: 'var(--green-2)', fontWeight: 600 }}>
                Forgot password?
              </a>
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                background: 'rgba(226,58,58,0.08)',
                border: '1px solid rgba(226,58,58,0.25)',
                color: '#b3261e',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <Button type="submit" loading={loading} block>
            {isSignup ? 'Create account' : 'Sign in'}
          </Button>

          {isSignup && (
            <p className="text-center" style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              By signing up you agree to our{' '}
              <a href="#" style={{ color: 'var(--ink-2)', textDecoration: 'underline' }}>Terms</a> and{' '}
              <a href="#" style={{ color: 'var(--ink-2)', textDecoration: 'underline' }}>Privacy Policy</a>.
            </p>
          )}
        </form>
      )}

      <div
        className="text-center"
        style={{
          marginTop: 20,
          paddingTop: 18,
          borderTop: '1px solid var(--line)',
          fontSize: 13,
          color: 'var(--muted)',
        }}
      >
        {isSignup ? (
          <>
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className="font-semibold"
              style={{ color: 'var(--green-2)' }}
            >
              Sign in
            </button>
          </>
        ) : (
          <>
            Don&apos;t have an account?{' '}
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className="font-semibold"
              style={{ color: 'var(--green-2)' }}
            >
              Sign up
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

function RoleCard({ icon, title, desc, iconBg, hoverBorder, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-3.5"
      style={{
        padding: 18,
        borderRadius: 14,
        border: '1px solid var(--line)',
        background: '#fff',
        transition: 'border-color .15s, transform .15s, box-shadow .15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = hoverBorder;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--line)';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span
        className="inline-flex items-center justify-center rounded-xl flex-shrink-0"
        style={{ width: 48, height: 48, background: iconBg, fontSize: 24 }}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="flex-1">
        <span className="block font-bold" style={{ fontSize: 16, color: 'var(--ink)' }}>{title}</span>
        <span className="block" style={{ marginTop: 2, fontSize: 13, color: 'var(--muted)' }}>{desc}</span>
      </span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.4">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}
